import { Stack, Textarea, Button, HStack, Spacer } from '@chakra-ui/react'
import type { FC, FormEventHandler } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'

import type { DirectionType } from '../aiNodeMenu'

import { toaster } from '@app/components/ui/toaster'
import { useStore } from '@app/store'
import type { DomainObject, DomainNode } from '@app/types'
import { AiNodesGenerate } from '@graphql/documents/ai_assistant.graphql'
import type { AiNodesGenerateMutation, AiNodesGenerateMutationVariables } from '@graphql/queries'

interface SuggestNodesPanelProps {
  node: DomainNode
  domainObject: DomainObject
  aiAssistantSlug: string
  direction: DirectionType
  handle: 'top' | 'bottom' | 'left' | 'right'
  onSuccess?: () => void
  handleBack?: () => void
  setLoading: (loading: boolean) => void
  loading: boolean
}

const SuggestNodesPanel: FC<SuggestNodesPanelProps> = ({
  node,
  aiAssistantSlug,
  direction,
  handle,
  onSuccess,
  handleBack,
  setLoading,
  loading
}) => {
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const actionMutation = useStore.use.actionMutation()

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()
      setLoading(true)
      try {
        const result = await actionMutation<AiNodesGenerateMutation, AiNodesGenerateMutationVariables>(
          AiNodesGenerate,
          {
            nodeId: node.nodeId,
            aiAssistantSlug,
            direction,
            handle,
            additionalInstructions
          }
        )
        if (result.error) {
          if (result.error.networkError) {
            toaster.create({ title: 'Error', description: result.error.networkError.message, type: 'error' })
          } else {
            const errors = result.error.graphQLErrors.map((err) => err.message).join(', ')
            toaster.create({ title: 'Error', description: errors, type: 'error' })
          }
        } else {
          toaster.create({ title: 'Success', description: 'Connected metrics suggested successfully', type: 'success' })
          setAdditionalInstructions('')
          if (onSuccess) {
            onSuccess()
          }
        }
      } catch (err) {
        toaster.create({ title: 'Error', description: err.message, type: 'error' })
      } finally {
        setLoading(false)
      }
    },
    [additionalInstructions, aiAssistantSlug, direction, handle, node.nodeId, onSuccess, setLoading, actionMutation]
  )

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <Textarea
          id="additionalInstructions"
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          placeholder="Optional: Add any extra context or instructions for the AI or leave blank if you're feeling lucky."
          rows={6}
          disabled={loading}
        />
        <HStack>
          <Button variant="ghost" onClick={handleBack} size="2xs">
            ← Back
          </Button>
          <Spacer />
          <Button type="submit" colorScheme="purple" size="xs" loading={loading} disabled={loading}>
            Suggest
          </Button>
        </HStack>
      </Stack>
    </form>
  )
}

export default SuggestNodesPanel
